import { useState, useRef, useEffect } from "react";
import ModalComponent from "../../shared/Modal";
import { getCustomerById } from "../../services/customer-service";
import { useNavigate } from "react-router-dom";
import { Forms } from "../../shared/forms/forms";
import { getLastIncompleteTicket, createTicket } from "../../services/tickets-service";

export const CreateTicketModal = ({
  isOpen,
  setIsOpen,
  customers,
  assignedEquipment,
  equipment
}) => {
  const [locations, setLocations] = useState([]);
  const [isYardService, setIsYardService] = useState(false);
  const [fields, setFields] = useState([]);
  const [createDisabled, setCreateDisabled] = useState(false);
  const formRef = useRef();
  const navigate = useNavigate();

  // Helper to build form fields, seeded with optional ticket & location list
  const buildFields = (ticket = {}, locs = locations) => {
    const svc = ticket.serviceTypes || [];
    const yard = svc.includes("Yard");
    setIsYardService(yard);

    const selectCustomer = customers.map(c => ({ value: c.id, label: c.name }));
    const selectEquipment = equipment.map(e => ({ value: e.id, label: e.name }));
    const selectLocation = locs.map(l => ({ value: l.id, label: l.displayName }));

    return [
      {
        label: "Service Type",
        type: "multiSelect",
        name: "serviceTypes",
        required: true,
        selectOptions: [
          { label: "Rods and Tubing", value: "RodsAndTubing" },
          { label: "Workover", value: "Workover" },
          { label: "P&A", value: "PAndA" },
          { label: "Completion", value: "Completion" },
          { label: "Stand By", value: "StandBy" },
          { label: "Yard", value: "Yard" },
          { label: "Roustabout", value: "Roustabout" }
        ],
        value: svc,
        onChange: sel => setIsYardService(sel.includes("Yard")),
        size: yard ? 4 : 6
      },
      {
        label: "Equipment Name",
        type: "select",
        name: "equipmentId",
        required: true,
        selectOptions: selectEquipment,
        value: ticket.equipmentId || assignedEquipment?.id || "",
        size: yard ? 4 : 6
      },
      {
        label: "Execution Date",
        type: "datetime",
        name: "executionDate",
        required: true,
        value: new Date(),
        size: 4
      },
      !yard && {
        label: "Customer Name",
        type: "autocomplete",
        name: "customerId",
        selectOptions: selectCustomer,
        value: ticket.customerId || "",
        onChange: custId => {
          if (!custId) {
            setLocations([]);
            setFields(buildFields({ ...ticket, customerId: "", customerLocationId: "" }, []));
            return;
          }
          getCustomerById(custId).then(res => {
            const locs = res.locations;
            const match = locs.find(l => l.displayName === ticket.locationName);
            setLocations(locs);
            setFields(
              buildFields(
                { ...ticket, customerId: custId, customerLocationId: match?.id || "" },
                locs
              )
            );
          });
        },
        size: 4
      },
      !yard && {
        label: "Customer Location",
        type: "select",
        name: "customerLocationId",
        selectOptions: selectLocation,
        value: ticket.customerLocationId || "",
        size: 4
      }
    ].filter(Boolean);
  };

  // On open: fetch last incomplete, then seed or blank
  useEffect(() => {
    if (!isOpen) return;

    setLocations([]);
    setFields([]);
    setIsYardService(false);

    getLastIncompleteTicket()
      .then(ticket => {
        if (ticket) {
          return getCustomerById(ticket.customerId).then(res => {
            const locs = res.locations;
            const match = locs.find(l => l.displayName === ticket.locationName);
            setLocations(locs);
            setFields(
              buildFields(
                { ...ticket, customerLocationId: match?.id || "" },
                locs
              )
            );
          });
        } else {
          setFields(buildFields());
        }
      })
      .catch(() => {
        setFields(buildFields());
      });
  }, [isOpen, customers, equipment]);

  const handleConfirm = () => formRef.current.submit();

  const onSubmit = data => {
    setCreateDisabled(true);
    data.executionDate = data.executionDate.toISOString().split("T")[0];
    createTicket(data)
      .then(id => navigate(`/dashboard/tickets/edit/${id}`))
      .finally(() => {
        setCreateDisabled(false);
        setIsOpen(false);
      });
  };

  const actions = [
    {
      onClick: handleConfirm,
      label: "Confirm",
      disabled: createDisabled,
      variant: "contained",
      color: "primary"
    },
    {
      onClick: () => setIsOpen(false),
      label: "Cancel",
      variant: "outlined",
      color: "default"
    }
  ];

  return (
    <ModalComponent
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title="Create Ticket"
      actions={actions}
      closeOnOutsideClick={false}
      isMedium
    >
      <Forms ref={formRef} fields={fields} onSubmit={onSubmit} />
    </ModalComponent>
  );
};
